import { useEffect, useRef } from 'react';
import useSession from 'hooks/call-queue/useSession';
import useApi from 'hooks/useApi';
// Bundled, not fetched. The rest of the app streams this from a hardcoded
// co.quikstep.dev S3 URL, which ships a dev bucket to production and puts a
// network round-trip in front of a sound that has to play immediately - while
// the very same file already sits in src/assets/audios.
import newMessageTone from 'assets/audios/ping.mp3';

/**
 * Ping + toast when the other side sends a chat message during a call.
 *
 * Mounted globally (MainLayout) rather than inside ChatWindow on purpose: the
 * chat panel only exists while the agent is sitting on /session/{id}, so
 * minimising the call or stepping away to another page meant messages arrived
 * in complete silence and were only discovered on the way back.
 *
 * `VideoSessionContext` keeps streaming `messages` for the active callUid no
 * matter which route is showing, so watching it here covers every case with one
 * listener and no duplicate alerts.
 */
const CallMessageAlert = () => {
    const { messages, callUid } = useSession();
    const { user, showToast } = useApi();

    const pingRef = useRef<HTMLAudioElement | null>(null);
    // Messages already present when we started watching this call. Starts null
    // so the existing history is never announced, only genuinely new arrivals.
    const seenCount = useRef<number | null>(null);
    const watchedCall = useRef<string | null>(null);

    if (!pingRef.current) {
        pingRef.current = new Audio(newMessageTone);
    }

    // A different call (or no call) means a fresh baseline.
    useEffect(() => {
        if (watchedCall.current !== callUid) {
            watchedCall.current = callUid ?? null;
            seenCount.current = null;
        }
    }, [callUid]);

    useEffect(() => {
        if (!callUid) return;

        if (seenCount.current === null) {
            seenCount.current = messages.length;
            return;
        }
        if (messages.length <= seenCount.current) {
            seenCount.current = messages.length;
            return;
        }

        const fresh = messages
            .slice(seenCount.current)
            .filter((m) => m.uid && m.uid !== user?.uid);
        seenCount.current = messages.length;
        if (fresh.length === 0) return;

        try {
            pingRef.current!.currentTime = 0;
            pingRef.current!.play().catch(() => undefined);
        } catch (e) {
            // Autoplay can be blocked before any interaction; the toast still shows.
        }

        const last = fresh[fresh.length - 1];
        const who = last.fromName?.trim() || 'New message';
        showToast(`${who}: ${last.message}`, 'info');
    }, [messages, callUid, user?.uid, showToast]);

    return null;
};

export default CallMessageAlert;
